import React, { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Gem, Coins, Send, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import GiftIcon from "./GiftIcon";
import { useGiftBurst } from "./GiftBurst";

const COLLECTION_META = {
  food:        { label: "Food",          tagline: "Comfort classics" },
  dessert:     { label: "Dessert",       tagline: "Sweet things" },
  business:    { label: "Business",      tagline: "Support local biz" },
  hidden_gem:  { label: "Hidden Gem",    tagline: "Platform exclusive" },
  legendary:   { label: "Legendary",     tagline: "Full-screen moments" },
};

const RARITY_COLOR = {
  common:    "text-zinc-300",
  rare:      "text-cyan-300",
  epic:      "text-purple-300",
  legendary: "text-yellow-300",
  mythic:    "text-pink-300",
};

/**
 * GiftSheet — universal gift picker.
 * Props:
 *   open / onOpenChange
 *   recipient        — { user_id, name, picture? } — who receives the gift
 *   targetKind       — "community_post" | "post" | "story" | "comment" |
 *                      "vendor" | "livestream" | "profile"
 *   targetId         — id of the target object (post_id, comment_id, vendor_id, etc.)
 *   onSent(tx)       — optional callback after a successful send
 */
export default function GiftSheet({ open, onOpenChange, recipient, targetKind, targetId, onSent }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const burst = useGiftBurst();
  const [catalog, setCatalog] = useState(null);
  const [walletCredits, setWalletCredits] = useState(0);
  const [activeCollection, setActiveCollection] = useState("food");
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Load catalog + wallet whenever the sheet opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: cat }, { data: w }] = await Promise.all([
          api.get("/gifts/catalog"),
          api.get("/wallet/me"),
        ]);
        if (cancelled) return;
        setCatalog(cat);
        setWalletCredits(w.wallet?.credits || 0);
      } catch (e) {
        toast.error("Couldn't load gifts");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const collections = catalog?.collections || {};
  const list = collections[activeCollection] || [];
  const canAfford = selected && walletCredits >= selected.cost_credits;

  const send = async () => {
    if (!selected || sending) return;
    if (!recipient?.user_id) {
      toast.error("No recipient");
      return;
    }
    if (recipient.user_id === user?.user_id) {
      toast.error("You can't gift yourself");
      return;
    }
    if (!canAfford) {
      toast.error("Not enough credits — top up first");
      navigate("/credits");
      onOpenChange(false);
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/gifts/send", {
        gift_id: selected.gift_id,
        recipient_id: recipient.user_id,
        target_kind: targetKind,
        target_id: targetId,
        message: message.trim() || undefined,
      });
      toast.success(`Sent ${selected.name} ✨`);
      // Play the burst overlay so the sender sees the celebration too
      burst.trigger({
        gift: { ...selected, ...(data.tx?.gift_snapshot || {}) },
        sender_name: "You",
        message: message.trim(),
      });
      setWalletCredits(data.new_balance);
      // Tell the rest of the UI (top-bar pill, wallet page, etc.) to refresh.
      window.dispatchEvent(new CustomEvent("hg:wallet-changed", { detail: { credits: data.new_balance } }));
      setMessage("");
      setSelected(null);
      onSent?.(data.tx);
      onOpenChange(false);
    } catch (err) {
      const code = err.response?.status;
      const detail = err.response?.data?.detail;
      if (code === 402) {
        toast.error("Not enough credits — top up first");
        navigate("/credits");
        onOpenChange(false);
      } else {
        toast.error(detail || "Couldn't send gift");
      }
    } finally {
      setSending(false);
    }
  };

  const collectionTabs = Object.keys(collections);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="bg-zinc-950 border-t border-white/10 text-white rounded-t-3xl p-0 max-h-[92dvh] overflow-y-auto"
        data-testid="gift-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-white/10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-white text-xl font-black tracking-tighter inline-flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-400" /> Send a gift
              </SheetTitle>
              <SheetDescription className="text-zinc-400 text-sm truncate">
                {recipient ? <>To <span className="text-white font-bold">{recipient.name}</span></> : "Pick a recipient"}
              </SheetDescription>
            </div>
            <div className="shrink-0 rounded-full bg-white/5 border border-white/10 px-3 h-9 inline-flex items-center gap-1.5">
              <Coins className="w-4 h-4 text-yellow-300" />
              <span className="text-sm font-bold" data-testid="gift-sheet-balance">{walletCredits.toLocaleString()}</span>
              <button
                onClick={() => {
                  onOpenChange(false);
                  navigate("/credits");
                }}
                className="ml-1 text-[10px] uppercase tracking-widest font-bold text-pink-300 hover:text-pink-200"
                data-testid="gift-sheet-topup-link"
              >
                Top up
              </button>
            </div>
          </div>
        </SheetHeader>

        <div className="px-5 pt-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="gift-collection-tabs">
          {collectionTabs.map((c) => {
            const meta = COLLECTION_META[c] || { label: c };
            const active = activeCollection === c;
            return (
              <button
                key={c}
                onClick={() => {
                  setActiveCollection(c);
                  setSelected(null);
                }}
                className={`shrink-0 rounded-full h-9 px-4 text-xs font-bold uppercase tracking-widest border transition-colors ${
                  active
                    ? "bg-pink-500/20 text-pink-200 border-pink-400/40"
                    : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
                }`}
                data-testid={`gift-tab-${c}`}
              >
                {meta.label}
              </button>
            );
          })}
        </div>

        <div className="px-5 py-4 grid grid-cols-4 sm:grid-cols-6 gap-2.5" data-testid="gift-grid">
          {list.map((g) => {
            const sel = selected?.gift_id === g.gift_id;
            const broke = walletCredits < g.cost_credits;
            return (
              <button
                key={g.gift_id}
                onClick={() => setSelected(g)}
                className={`relative aspect-square rounded-2xl border p-2 flex flex-col items-center justify-center gap-1 transition-all ${
                  sel
                    ? "border-pink-400/60 bg-pink-500/10 scale-105 rarity-mythic"
                    : "border-white/10 bg-white/[0.04] hover:border-pink-400/30 hover:bg-white/[0.07]"
                } ${broke ? "opacity-55" : ""}`}
                data-testid={`gift-card-${g.gift_id}`}
              >
                <GiftIcon gift={g} size={36} paused={!sel} />
                <p className="text-[10px] font-bold text-center truncate w-full">{g.name}</p>
                <p className={`text-[10px] font-black inline-flex items-center gap-0.5 ${RARITY_COLOR[g.rarity]}`}>
                  <Coins className="w-2.5 h-2.5" />
                  {g.cost_credits}
                </p>
                {g.full_screen && (
                  <span className="absolute -top-1 -right-1 text-[8px] font-black bg-yellow-400 text-black rounded-full px-1.5 py-0.5 uppercase tracking-widest">
                    Hero
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected gift detail + send */}
        {selected && (
          <div className="px-5 pb-6 pt-2 border-t border-white/10" data-testid="gift-send-panel">
            <div className="flex items-center gap-3 mb-3">
              <div className={`shrink-0 rounded-2xl border border-white/10 bg-zinc-900 p-3 rarity-${selected.rarity}`}>
                <GiftIcon gift={selected} size={48} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-black tracking-tight">{selected.name}</p>
                <p className="text-xs text-zinc-400">{selected.description}</p>
                <p className={`text-[10px] uppercase tracking-widest font-black mt-1 ${RARITY_COLOR[selected.rarity]}`}>
                  {selected.rarity} · {selected.cost_credits} credits · creator gets {Math.round(selected.cost_credits * (catalog?.recipient_share_pct || 70) / 100)} cr
                </p>
              </div>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 200))}
              placeholder="Add a message (optional)…"
              rows={2}
              className="w-full px-3 py-2 rounded-2xl bg-white/5 border border-white/10 text-sm placeholder:text-zinc-500 outline-none focus:border-pink-400"
              data-testid="gift-message"
            />
            <Button
              onClick={send}
              disabled={sending || !canAfford}
              className="mt-3 w-full h-12 rounded-full bg-gradient-to-r from-pink-500 to-cyan-400 hover:from-pink-400 hover:to-cyan-300 text-white font-black tracking-tight text-base shadow-[0_0_24px_-6px_rgba(236,72,153,.7)] disabled:opacity-50"
              data-testid="gift-send-btn"
            >
              {sending
                ? "Sending…"
                : !canAfford
                  ? `Need ${(selected.cost_credits - walletCredits).toLocaleString()} more credits`
                  : <><Send className="w-4 h-4 mr-2" /> Send {selected.name} · {selected.cost_credits}</>}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
